import { useState, useRef, useEffect, useCallback } from 'react';
import { runLlmMode, connectLlmModeStream, pauseLlmMode } from '../services/api';
import { DEFAULT_COUNSELLOR_MODE } from '../counsellorModes';

function useLlmMode({
  profileIdx,
  conversationLength,
  counsellorModes,
  counsellorMode,
  confirmDialog,
  resolveClientType,
  onConversationStart,
  onSessionRegistered,
  onSystemMessagesUpdate,
  onTurnClassification,
  onRevealedItemsUpdate,
  onStageMetricsUpdate,
  onSessionEnd,
  notify,
  setLoading,
  setConversation,
  setSessionId,
  setSessionEnded,
  setEndReason,
  scrollToBottom,
}) {
  const [llmMode, setLlmMode] = useState(false);
  const [llmPaused, setLlmPaused] = useState(false);
  const [llmFinishingUtterance, setLlmFinishingUtterance] = useState(false);
  const llmStreamRef = useRef(null);

  const abortLlmStream = useCallback(() => {
    if (llmStreamRef.current) {
      llmStreamRef.current.abort();
      llmStreamRef.current = null;
    }
  }, []);

  const startLlmModeStream = useCallback((startedSessionId = null, streamCounsellorMode = null) => {
    abortLlmStream();

    let currentSessionId = startedSessionId;
    let sessionEndHandled = false;

    const finishLlmSession = (reason) => {
      if (sessionEndHandled) {
        return;
      }
      sessionEndHandled = true;
      setSessionEnded(true);
      setEndReason(reason);
      if (onSessionEnd && currentSessionId) {
        onSessionEnd(currentSessionId, {
          mode: 'llm_mode',
          counsellorMode: streamCounsellorMode || counsellorMode || DEFAULT_COUNSELLOR_MODE,
        });
      }
    };

    const stream = connectLlmModeStream(
      (data) => {
        setConversation((prev) => [...prev, { speaker: 'counsellor', text: data.text }]);
        // Same "Skill Used" classification as a human counsellor turn, minus guidance.
        onTurnClassification?.(data.automiti_labels || [], { counsellorIsLlm: true });
        if (!currentSessionId) {
          currentSessionId = data.session_id || startedSessionId;
        }
        if (currentSessionId) {
          setSessionId(currentSessionId);
        }
      },
      (data) => {
        setConversation((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.speaker === 'client' && last.isStreaming) {
            updated[updated.length - 1] = { ...last, text: last.text + data.token };
          } else {
            updated.push({ speaker: 'client', text: data.token, isStreaming: true });
          }
          return updated;
        });
      },
      (data) => {
        setConversation((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.isStreaming) {
            updated[updated.length - 1] = { speaker: 'client', text: last.text };
          }
          return updated;
        });

        setLlmFinishingUtterance(false);
        setTimeout(() => scrollToBottom?.(), 50);

        if (onSystemMessagesUpdate && data.system_messages) {
          onSystemMessagesUpdate(
            data.system_messages.counsellor || [],
            data.system_messages.client || [],
            data.stage_metrics || null,
            data.working_alliance_series || [],
            data.all_tasks_series || [],
            { automitiLabels: data.automiti_labels || [], evaluation: null, counsellorIsLlm: true }
          );
        }
        if (onRevealedItemsUpdate && data.revealed_items) {
          onRevealedItemsUpdate(data.revealed_items);
        }
        if (onStageMetricsUpdate && data.stage_metrics) {
          onStageMetricsUpdate(data.stage_metrics);
        }

        if (data.session_ended) {
          finishLlmSession(data.end_reason || 'Session ended');
        }
      },
      (data) => {
        llmStreamRef.current = null;
        setLlmMode(false);
        setLlmPaused(false);
        setLlmFinishingUtterance(false);
        if (data.reason !== 'cancelled') {
          finishLlmSession('LLM conversation completed');
        }
      },
      (errorMsg) => {
        console.error('LLM stream error:', errorMsg);
        llmStreamRef.current = null;
        notify(`LLM Mode error: ${errorMsg}`);
        setLlmMode(false);
        setLlmPaused(false);
        setLlmFinishingUtterance(false);
      }
    );

    llmStreamRef.current = stream;
  }, [
    abortLlmStream,
    counsellorMode,
    notify,
    onRevealedItemsUpdate,
    onSessionEnd,
    onStageMetricsUpdate,
    onSystemMessagesUpdate,
    onTurnClassification,
    scrollToBottom,
    setConversation,
    setEndReason,
    setSessionEnded,
    setSessionId,
  ]);

  const handleLlmMode = useCallback(async (requestedCounsellorMode = null) => {
    const activeCounsellorMode = requestedCounsellorMode || counsellorMode || DEFAULT_COUNSELLOR_MODE;
    const mode =
      counsellorModes.find((entry) => entry.value === activeCounsellorMode) ||
      counsellorModes.find((entry) => entry.value === DEFAULT_COUNSELLOR_MODE);
    const clientName = profileIdx === -1 ? 'Custom Client' : `Client ${profileIdx}`;
    const hasExisting = conversationLength > 0;

    if (!mode) {
      notify('Unable to start LLM mode.');
      return;
    }

    const confirmed = await confirmDialog(
      `Let our ${mode.label} Counsellor take over?\n\n` +
      (hasExisting
        ? 'The AI counsellor will continue from where you left off.\n'
        : 'This will start a new conversation between two AI agents:\n') +
      `- ${mode.label} Counsellor (LLM)\n` +
      `- ${clientName} (LLM)\n\n` +
      `${mode.description}\n\n` +
      'Messages will appear in real-time as they\'re generated.\n\n' +
      'Continue?'
    );

    if (!confirmed) return;

    if (!confirmed) return;

    try {
      setLoading(true);
      setSessionEnded(false);
      setLlmMode(true);

      onConversationStart?.();
      const sessionData = await runLlmMode(profileIdx, resolveClientType(), true, 120, activeCounsellorMode);
      if (sessionData.session_id) {
        setSessionId(sessionData.session_id);
        onSessionRegistered?.(sessionData.session_id, {
          mode: 'llm_mode',
          counsellorMode: activeCounsellorMode,
        });
      }
      startLlmModeStream(sessionData.session_id, activeCounsellorMode);
      setLoading(false);
    } catch (err) {
      console.error('Failed to start LLM mode:', err);
      notify('Failed to start LLM mode. Please check that LM Studio is running.');
      setLlmMode(false);
      setLoading(false);
    }
  }, [
    confirmDialog,
    counsellorMode,
    conversationLength,
    counsellorModes,
    notify,
    onConversationStart,
    onSessionRegistered,
    profileIdx,
    resolveClientType,
    setLoading,
    setSessionId,
    setSessionEnded,
    startLlmModeStream,
  ]);

  const handlePauseLlmMode = useCallback(async () => {
    try {
      if (!llmPaused) {
        setLlmFinishingUtterance(true);
      }
      const result = await pauseLlmMode();
      setLlmPaused(result.paused || false);
      if (!result.paused) {
        setLlmFinishingUtterance(false);
      }
    } catch (err) {
      console.error('Failed to pause/resume LLM mode:', err);
      notify('Failed to pause/resume LLM mode');
    }
  }, [llmPaused, notify]);

  useEffect(() => {
    if (!llmPaused) {
      setLlmFinishingUtterance(false);
    }
  }, [llmPaused]);

  return {
    llmMode,
    llmPaused,
    llmFinishingUtterance,
    setLlmMode,
    setLlmPaused,
    setLlmFinishingUtterance,
    abortLlmStream,
    handleLlmMode,
    handlePauseLlmMode,
  };
}

export default useLlmMode;
